import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from "vue";
import Dialog from "@/components/vuetify-wrappers/NjDialog.vue";
import NjBtn from "@/components/vuetify-wrappers/NjButton.vue";
import { useSessionStore } from "@/pinia/session";
import { ApiService, LogService, SnackbarService } from "@/services";
import { getDefaultPptxCustomization } from "@/utils/PptxCustomizationUtils";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileDownloadControls',
  props: {
    docxUrl: null,
    pdfUrl: null,
    pptxUrl: null
  },
  setup(__props: any) {

const props = __props as {
  docxUrl: string;
  pdfUrl: string;
  pptxUrl: string;
};



const sessionStore = useSessionStore();
const hasCustomLogo = computed(() => sessionStore.hasCustomLogo);
const pcTemplates = computed(() => sessionStore.user?.pcTemplates ?? []);
const selectedFormat = ref<"docx" | "pdf" | "pptx" | "">("");
const selectedCustomization = ref("standard");
const isDownloadLoading = ref(false);
const downloadAnchor = ref<HTMLAnchorElement | null>(null);
const requestCustomLogoDialog = ref(false);

const formatOptions = computed(() => {
  const items: { text: string; value: "docx" | "pdf" | "pptx" }[] = [];
  if (props.docxUrl)
    items.push({ text: "Microsoft Word (.docx)", value: "docx" });
  if (props.pptxUrl)
    items.push({ text: "PowerPoint (.pptx)", value: "pptx" });
  if (props.pdfUrl) items.push({ text: "PDF", value: "pdf" });
  return items;
});

const customizationOptions = computed(() => {
  if (selectedFormat.value === "pdf") {
    return [{ text: "Standard Template", value: "standard" }];
  }
  if (selectedFormat.value === "docx") {
    return [
      { text: "Standard Template", value: "standard" },
      { text: "Custom Logo", value: "custom_logo" },
    ];
  }
  if (selectedFormat.value === "pptx") {
    const items: { text: string; value: string }[] = [
      { text: "Standard Template", value: "standard" },
    ];
    for (const template of pcTemplates.value) {
      items.push({
        text: `Custom Template: ${template.title}`.trim(),
        value: `pc_template:${template.id}`,
      });
    }
    return items;
  }
  return [{ text: "Standard Template", value: "standard" }];
});

const isCustomizationDisabled = computed(() => {
  if (selectedFormat.value === "pdf") return true;
  if (selectedFormat.value === "pptx") return pcTemplates.value.length === 0;
  return false;
});

const selectedDownloadUrl = computed(() => {
  if (selectedFormat.value === "docx") {
    return selectedCustomization.value === "custom_logo"
      ? `${props.docxUrl}?custom_logo=True`
      : props.docxUrl;
  }
  if (selectedFormat.value === "pdf") return props.pdfUrl;
  if (selectedFormat.value === "pptx") {
    let url = props.pptxUrl;
    const selection = selectedCustomization.value || "";
    if (selection.startsWith("pc_template:")) {
      const id = selection.split(":")[1];
      if (id) {
        try {
          const parsedUrl = new URL(url, window.location.origin);
          parsedUrl.searchParams.set("pc_template_id", String(id));
          url = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
        } catch (error) {
          const separator = url.includes("?") ? "&" : "?";
          url = `${url}${separator}pc_template_id=${encodeURIComponent(
            String(id)
          )}`;
        }
      }
    }
    return url;
  }
  return "";
});

const isDownloadDisabled = computed(() => {
  if (!selectedFormat.value) return true;
  if (selectedFormat.value === "docx" && !props.docxUrl) return true;
  if (selectedFormat.value === "pdf" && !props.pdfUrl) return true;
  if (selectedFormat.value === "pptx" && !props.pptxUrl) return true;
  return false;
});

watch(
  [
    () => props.docxUrl,
    () => props.pdfUrl,
    () => props.pptxUrl,
    () => hasCustomLogo.value,
  ],
  () => {
    if (!selectedFormat.value) {
      if (props.docxUrl) selectedFormat.value = "docx";
      else if (props.pptxUrl) selectedFormat.value = "pptx";
      else if (props.pdfUrl) selectedFormat.value = "pdf";
    }

    if (selectedFormat.value === "docx") {
      selectedCustomization.value = hasCustomLogo.value
        ? "custom_logo"
        : "standard";
    } else if (selectedFormat.value === "pdf") {
      selectedCustomization.value = "standard";
    } else if (selectedFormat.value === "pptx") {
      selectedCustomization.value = getDefaultPptxCustomization(
        pcTemplates.value
      );
    }
  },
  { immediate: true }
);

watch(selectedFormat, (format) => {
  if (format === "pdf") {
    selectedCustomization.value = "standard";
  } else if (format === "docx") {
    selectedCustomization.value = hasCustomLogo.value
      ? "custom_logo"
      : "standard";
  } else if (format === "pptx") {
    selectedCustomization.value = getDefaultPptxCustomization(
      pcTemplates.value
    );
  }
});

watch(
  pcTemplates,
  () => {
    if (selectedFormat.value === "pptx") {
      selectedCustomization.value = getDefaultPptxCustomization(
        pcTemplates.value
      );
    }
  },
  { deep: true }
);

const downloadSelected = (): void => {
  if (
    selectedFormat.value === "docx" &&
    selectedCustomization.value === "custom_logo" &&
    !hasCustomLogo.value
  ) {
    requestCustomLogoDialog.value = true;
    return;
  }

  const url = selectedDownloadUrl.value;
  if (!url) return;

  isDownloadLoading.value = true;
  setTimeout(() => {
    try {
      if (downloadAnchor.value) {
        downloadAnchor.value.href = url;
        downloadAnchor.value.click();
      } else {
        window.open(url, "_blank");
      }
    } finally {
      isDownloadLoading.value = false;
    }
  }, 250);
};

const requestCustomLogo = (): void => {
  ApiService.postUserContact("", true).then((response) => {
    if (response.statusCode === 200) {
      LogService.info(`Profile.requestCustomLogo is successful.`);
      SnackbarService.displaySnackbar(
        "Request for custom logo successfully submitted.",
        "primary"
      );
    } else {
      LogService.error(
        `Profile.requestCustomLogo failed: '${response.error_message}'`
      );
      SnackbarService.displaySnackbar("An error occurred, please try again.");
    }
  });

  requestCustomLogoDialog.value = false;
};

return { __sfc: true,props, sessionStore, hasCustomLogo, pcTemplates, selectedFormat, selectedCustomization, isDownloadLoading, downloadAnchor, requestCustomLogoDialog, formatOptions, customizationOptions, isCustomizationDisabled, selectedDownloadUrl, isDownloadDisabled, downloadSelected, requestCustomLogo, Dialog, NjBtn }
}

})